import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Send, Search, ChevronLeft, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useAuthStore } from '@/stores/authStore'
import { useSocket } from '@/hooks/useSocket'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { formatDistanceToNow } from '@/lib/utils'

interface Conversation {
  userId: string
  username: string
  avatar?: string
  lastMessage?: { content: string; createdAt: string; isRead: boolean }
  unreadCount: number
}

interface Message {
  _id: string
  sender: string
  receiver: string
  content: string
  createdAt: string
  read: boolean
}

export default function MessagesPage() {
  const { t } = useTranslation()
  const { userId: paramUserId } = useParams()
  const { user } = useAuthStore()
  const socket = useSocket()
  const queryClient = useQueryClient()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const readMessageIdsRef = useRef<Set<string>>(new Set())
  const lastSelectedUserIdRef = useRef<string | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(paramUserId || null)
  const [message, setMessage] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [isTyping, setIsTyping] = useState(false)
  const [conversationsPage, setConversationsPage] = useState(1)
  const [messagesPage, setMessagesPage] = useState(1)
  const [allMessages, setAllMessages] = useState<Message[]>([])
  const [hasMoreMessages, setHasMoreMessages] = useState(false)

  const { data: conversationsData } = useQuery({
    queryKey: ['conversations', conversationsPage],
    queryFn: () => api.get(`/messages/conversations?page=${conversationsPage}`).then(r => r.data)
  })
  const conversations = conversationsData?.conversations || []
  const hasMoreConversations = conversationsData?.pagination?.hasMore

  const { data: chatData } = useQuery({
    queryKey: ['messages', selectedUserId, messagesPage],
    queryFn: () => api.get(`/messages/${selectedUserId}?page=${messagesPage}`).then(r => r.data),
    enabled: !!selectedUserId,
    refetchInterval: messagesPage === 1 ? 5000 : false
  })

  const sendMessage = useMutation({
    mutationFn: async () => {
      const res = await api.post('/messages', { receiverId: selectedUserId, content: message })
      return res.data
    },
    onMutate: async () => {
      // Optimistically add the message so it appears instantly
      const tempId = `temp-${Date.now()}`
      const optimisticMessage: Message = {
        _id: tempId,
        sender: user?.id || '',
        receiver: selectedUserId || '',
        content: message.trim(),
        createdAt: new Date().toISOString(),
        read: true,
      }
      await queryClient.cancelQueries({ queryKey: ['messages', selectedUserId, messagesPage] })
      const previousMessages = allMessages
      setAllMessages(prev => [...prev, optimisticMessage])
      return { previousMessages, tempId }
    },
    onSuccess: () => {
      setMessage('')
      // Return to newest page and refresh so the server-confirmed message appears
      setMessagesPage(1)
      queryClient.invalidateQueries({ queryKey: ['messages', selectedUserId, 1] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
    onError: (err: any, _variables, context) => {
      // Rollback optimistic update on error
      if (context?.previousMessages) {
        setAllMessages(context.previousMessages)
      }
      toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
    },
  })

  // Merge paginated messages: page 1 replaces (newest), later pages prepend (older)
  useEffect(() => {
    if (!chatData?.messages) return
    setHasMoreMessages(chatData?.pagination?.hasMore || false)
    setAllMessages(prev => {
      if (messagesPage === 1) return chatData.messages
      // Avoid duplicates when prepending older pages
      const existingIds = new Set(prev.map(m => m._id))
      const older = chatData.messages.filter((m: Message) => !existingIds.has(m._id))
      return [...older, ...prev]
    })
  }, [chatData, messagesPage])

  useEffect(() => {
    setMessagesPage(1)
    setAllMessages([])
    setHasMoreMessages(false)
  }, [selectedUserId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [allMessages])

  // Emit typing:stop on unmount or when selectedUserId changes
  useEffect(() => {
    return () => {
      if (socket && selectedUserId) {
        socket.emit('typing:stop', { receiverId: selectedUserId })
      }
    }
  }, [socket, selectedUserId])

  useEffect(() => {
    if (!allMessages.length || !selectedUserId || !user?.id) return

    if (lastSelectedUserIdRef.current !== selectedUserId) {
      readMessageIdsRef.current.clear()
      lastSelectedUserIdRef.current = selectedUserId
    }

    const unreadMessages = allMessages.filter(
      (msg: Message) => msg.sender !== user.id && !msg.read && !readMessageIdsRef.current.has(msg._id)
    )

    if (unreadMessages.length === 0) return

    unreadMessages.forEach((msg: Message) => readMessageIdsRef.current.add(msg._id))

    Promise.all(
      unreadMessages.map((msg: Message) => api.put(`/messages/${msg._id}/read`))
    )
      .then(() => {
        setAllMessages(prev =>
          prev.map((m: Message) =>
            unreadMessages.some((um: Message) => um._id === m._id) ? { ...m, read: true } : m
          )
        )
        queryClient.invalidateQueries({ queryKey: ['conversations'] })
      })
      .catch(() => {
        unreadMessages.forEach((msg: Message) => readMessageIdsRef.current.delete(msg._id))
      })
  }, [allMessages, selectedUserId, user?.id, queryClient])

  // Real-time socket listeners
  useEffect(() => {
    if (!socket) return
    const handleReceive = (data: any) => {
      if (data.senderId === selectedUserId) {
        // Reset to newest page and refresh so pagination stays consistent
        setMessagesPage(1)
        queryClient.invalidateQueries({ queryKey: ['messages', selectedUserId, 1] })
      }
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    }
    const handleMessageRead = (data: any) => {
      setAllMessages(prev =>
        prev.map((m: Message) =>
          m._id === data.messageId ? { ...m, read: true } : m
        )
      )
    }
    const handleConversationUpdate = () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    }
    const handleTypingStart = (data: any) => {
      if (data.userId === selectedUserId) setIsTyping(true)
    }
    const handleTypingStop = (data: any) => {
      if (data.userId === selectedUserId) setIsTyping(false)
    }
    socket.on('message:receive', handleReceive)
    socket.on('message:read', handleMessageRead)
    socket.on('conversation:update', handleConversationUpdate)
    socket.on('typing:start', handleTypingStart)
    socket.on('typing:stop', handleTypingStop)
    return () => {
      socket.off('message:receive', handleReceive)
      socket.off('message:read', handleMessageRead)
      socket.off('conversation:update', handleConversationUpdate)
      socket.off('typing:start', handleTypingStart)
      socket.off('typing:stop', handleTypingStop)
    }
  }, [socket, selectedUserId, queryClient, user?.id])

  const handleSearch = async () => {
    if (!searchQuery.trim()) return
    try {
      const res = await api.get(`/users/search?username=${searchQuery}`)
      setSearchResults(res.data || [])
    } catch (err: any) {
      toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
    }
  }

  const selectedConversation = conversations?.find((c: Conversation) => c.userId === selectedUserId)

  return (
    <div className="max-w-4xl mx-auto h-[calc(100vh-4rem)] flex">
      {/* Sidebar */}
      <div className={`w-full md:w-80 border-r border-border bg-card/50 flex flex-col ${selectedUserId ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold mb-3">{t('messages.title')}</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={t('messages.searchPlaceholder')}
              className="w-full pl-9 pr-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
            />
          </div>
          {searchResults.length > 0 && (
            <div className="mt-2 space-y-1">
              {searchResults.map((u: any) => (
                <button
                  key={u._id}
                  onClick={() => { setSelectedUserId(u._id); setSearchResults([]); setSearchQuery('') }}
                  className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-secondary text-left text-sm"
                >
                  <Avatar className="w-8 h-8"><AvatarImage src={u.avatar} /><AvatarFallback>{u.username[0]}</AvatarFallback></Avatar>
                  {u.username}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {conversations?.map((conv: Conversation) => (
            <button
              key={conv.userId}
              onClick={() => setSelectedUserId(conv.userId)}
              className={`w-full flex items-center gap-3 p-4 text-left hover:bg-secondary/50 transition-colors ${
                selectedUserId === conv.userId ? 'bg-secondary' : ''
              }`}
            >
              <Avatar className="w-10 h-10">
                <AvatarImage src={conv.avatar} />
                <AvatarFallback>{conv.username[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{conv.username}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {conv.lastMessage?.content || t('messages.noMessages')}
                </p>
              </div>
              {conv.unreadCount > 0 && (
                <span className="w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                  {conv.unreadCount}
                </span>
              )}
            </button>
          ))}
          {hasMoreConversations && (
            <button
              type="button"
              onClick={() => setConversationsPage(p => p + 1)}
              className="w-full py-3 text-sm text-primary hover:bg-secondary/50 transition-colors"
            >
              {t('common.loadMore')}
            </button>
          )}
        </div>
      </div>

      {/* Chat */}
      {selectedUserId ? (
        <div className="flex-1 flex flex-col">
          <div className="flex items-center gap-3 p-4 border-b border-border">
            <button onClick={() => setSelectedUserId(null)} className="md:hidden">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <Link to={`/profile/${selectedConversation?.username || chatData?.user?.username || ''}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <Avatar className="w-8 h-8">
                <AvatarImage src={selectedConversation?.avatar} />
                <AvatarFallback>{selectedConversation?.username?.[0]}</AvatarFallback>
              </Avatar>
              <span className="font-medium">{selectedConversation?.username || t('messages.user')}</span>
            </Link>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {hasMoreMessages && (
              <div className="text-center py-2">
                <button
                  type="button"
                  onClick={() => setMessagesPage(p => p + 1)}
                  className="text-sm text-primary hover:underline"
                >
                  {t('messages.loadOlder')}
                </button>
              </div>
            )}
            <AnimatePresence>
              {allMessages.map((msg: Message) => (
                <motion.div
                  key={msg._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.sender === user?.id ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] px-4 py-2 rounded-2xl text-sm ${
                    msg.sender === user?.id
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-secondary rounded-bl-md'
                  }`}>
                    <p>{msg.content}</p>
                    <div className="flex items-center justify-end gap-1 mt-1">
                      <p className={`text-[10px] ${msg.sender === user?.id ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                        {formatDistanceToNow(msg.createdAt)}
                      </p>
                      {msg.sender === user?.id && msg.read && (
                        <Check className="w-3 h-3 text-primary-foreground/50" />
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            <div ref={messagesEndRef} />
          </div>

          {isTyping && (
            <div className="px-4 py-1 text-xs text-muted-foreground">
              {selectedConversation?.username} {t('messages.typing')}
            </div>
          )}
          <div className="p-4 border-t border-border flex gap-2">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onFocus={() => socket?.emit('typing:start', { receiverId: selectedUserId })}
              onBlur={() => socket?.emit('typing:stop', { receiverId: selectedUserId })}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage.mutate()}
              placeholder={t('messages.typeMessage')}
              className="flex-1 px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <Button size="icon" onClick={() => sendMessage.mutate()} disabled={!message.trim()}>
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center text-muted-foreground">
          <div className="text-center">
            <Send className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t('messages.selectChat')}</p>
          </div>
        </div>
      )}
    </div>
  )
}
